<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import { Loader2, RefreshCcw, X } from 'lucide-vue-next'
import { apiCreatePayment, apiQueryPaymentStatus } from '@/apis/membership'
import type { IPaymentPayload } from '@/types/apis/membership'
import { PaymentCurrency, PaymentProvider, PaymentScene, PaymentStatus } from '@/enums/membership'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import type { IPurchaseSummary, PurchaseBusinessType } from '../types'

// ---- Types ----

interface IPendingPayment {
  OrderNo: string
  ChannelOrderId: string
  PaymentProvider: PaymentProvider
  PayPalSdkUrl: string | null
  createdAt: number
}

// ---- Props / Emits ----

const props = defineProps<{
  visible: boolean
  businessType: PurchaseBusinessType
  planId: number
  rechargePoints?: number
  purchaseSummary: IPurchaseSummary | null
}>()

interface IPaymentResultPayload {
  orderNo: string
  paymentProvider: PaymentProvider
  channelOrderId: string
  status: PaymentStatus
  planId: number
}

const emit = defineEmits<{
  'update:visible': [value: boolean]
  paid: [payload: IPaymentResultPayload]
}>()

// ---- Setup ----

const { t } = useI18n()
const appStore = useAppStore()
const userStore = useUserStore()

const pollingIntervalMs = 2000
const pollingTimeoutMs = 10 * 60 * 1000

// ---- 弹窗内部状态（关闭时全部重置） ----

const selectedProvider = ref<PaymentProvider>(PaymentProvider.Alipay)
const creatingOrder = ref(false)
const orderCreated = ref(false)
const payPalContainer = ref<HTMLElement | null>(null)
const payPalButtonsInstance = ref<PayPalButtonsComponent | null>(null)
const isRenderingButton = ref(false)
const sdkLoadedSrc = ref('')
const sdkError = ref('')
const buttonRenderError = ref('')
const pendingPayment = ref<IPendingPayment | null>(null)
const pollTimer = ref<number | null>(null)
const queryLoading = ref(false)

// ---- Computed ----

const popupRound = computed(() => (appStore.isMobile ? '24px 24px 0 0' : '28px'))
const isPointsBusiness = computed(() => props.businessType === 'points')

// 根据支付方式和端形态返回对应的 PaymentScene
const paymentScene = computed(() => {
  if (selectedProvider.value === PaymentProvider.Alipay && !appStore.isMobile) {
    return PaymentScene.ALIPAY_PC
  }
  return PaymentScene.PAYPAL_OR_ALIPAYH5
})

const currentCurrency = computed(() =>
  selectedProvider.value === PaymentProvider.Alipay ? PaymentCurrency.CNY : PaymentCurrency.USD,
)

const displayPrice = computed(() => {
  if (!props.purchaseSummary) return '--'

  const isCny = currentCurrency.value === PaymentCurrency.CNY
  const symbol = isCny ? '¥' : '$'
  const price = isCny ? props.purchaseSummary.priceCny : props.purchaseSummary.priceUsd
  return `${symbol}${price.toFixed(2)}`
})

const paymentCurrencyLabel = computed(() => currentCurrency.value)

const summaryHint = computed(() => props.purchaseSummary?.validityDescription || props.purchaseSummary?.description || '')

const confirmBtnLabel = computed(() => {
  if (creatingOrder.value) return t('component.planPage.creatingOrder')
  if (selectedProvider.value === PaymentProvider.Alipay) return t('component.planPage.alipayPay')
  return t('component.planPage.paypalPay')
})

const isPayPalRendering = computed(
  () => orderCreated.value && selectedProvider.value === PaymentProvider.PayPal && isRenderingButton.value,
)

// ---- 状态清理 ----

const clearPollTimer = () => {
  if (pollTimer.value !== null) {
    window.clearInterval(pollTimer.value)
    pollTimer.value = null
  }
}

const destroyPayPalButtons = async () => {
  if (payPalButtonsInstance.value?.close) {
    try {
      await payPalButtonsInstance.value.close()
    } catch {}
  }
  payPalButtonsInstance.value = null
  if (payPalContainer.value) {
    payPalContainer.value.innerHTML = ''
  }
}

const resetState = async () => {
  clearPollTimer()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  await destroyPayPalButtons()
  selectedProvider.value = PaymentProvider.Alipay
  creatingOrder.value = false
  orderCreated.value = false
  sdkError.value = ''
  buttonRenderError.value = ''
  pendingPayment.value = null
  queryLoading.value = false
  isRenderingButton.value = false
}

// ---- 状态轮询 ----

const queryPaymentStatus = async (force = false) => {
  if (!pendingPayment.value || queryLoading.value) return
  if (!force && Date.now() - pendingPayment.value.createdAt > pollingTimeoutMs) {
    clearPollTimer()
    return
  }

  queryLoading.value = true
  try {
    const res = await apiQueryPaymentStatus({
      OrderNo: pendingPayment.value.OrderNo,
      PaymentProvider: pendingPayment.value.PaymentProvider,
      ChannelOrderId: pendingPayment.value.ChannelOrderId,
    })

    const status = res.Body.PaymentStatus as PaymentStatus

    if (res.Body.IsFinalStatus) {
      clearPollTimer()
      const currentPayment = pendingPayment.value

      if (status === PaymentStatus.Paid) {
        await userStore.refreshProfileInfo()
      }

      emit('paid', {
        orderNo: currentPayment.OrderNo,
        paymentProvider: currentPayment.PaymentProvider,
        channelOrderId: currentPayment.ChannelOrderId,
        status,
        planId: props.planId,
      })
      handleClose()
    }
  } catch (error) {
    console.error('queryPaymentStatus failed', error)
  } finally {
    queryLoading.value = false
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    void queryPaymentStatus(true)
  }
}

const startPolling = () => {
  clearPollTimer()
  pollTimer.value = window.setInterval(() => void queryPaymentStatus(), pollingIntervalMs)
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

// ---- PayPal ----

const loadPayPalSdk = async (sdkUrl: string) => {
  const win = window as Window & { paypal?: PayPalNamespace }
  if (win.paypal && sdkLoadedSrc.value === sdkUrl) return

  const existing = document.querySelector<HTMLScriptElement>('script[data-paypal-sdk="membership"]')
  if (existing) {
    existing.remove()
    win.paypal = undefined
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = sdkUrl
    script.async = true
    script.dataset.paypalSdk = 'membership'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('PayPal SDK load failed'))
    document.body.appendChild(script)
  })

  sdkLoadedSrc.value = sdkUrl
}

const renderPayPalButtons = async () => {
  if (!pendingPayment.value || !payPalContainer.value) return
  if (!pendingPayment.value.PayPalSdkUrl) {
    sdkError.value = t('component.planPage.noSdkUrl')
    return
  }

  isRenderingButton.value = true
  sdkError.value = ''
  buttonRenderError.value = ''

  try {
    await loadPayPalSdk(pendingPayment.value.PayPalSdkUrl)
    const win = window as Window & { paypal?: PayPalNamespace }
    if (!win.paypal) throw new Error('PayPal SDK unavailable')

    await destroyPayPalButtons()

    payPalButtonsInstance.value = win.paypal.Buttons({
      style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal', tagline: false, height: 44 },
      createOrder: () => {
        if (!pendingPayment.value?.ChannelOrderId) throw new Error('Missing PayPal channel order id')
        return pendingPayment.value.ChannelOrderId
      },
      onApprove: async () => void queryPaymentStatus(true),
      onCancel: async () => void queryPaymentStatus(true),
      onError: async (error: unknown) => {
        console.error('PayPal button error:', error)
        void queryPaymentStatus(true)
      },
    })

    await payPalButtonsInstance.value?.render(payPalContainer.value)
  } catch (error) {
    console.error(error)
    buttonRenderError.value = t('component.planPage.buttonRenderFailed')
  } finally {
    isRenderingButton.value = false
  }
}

// ---- 支付宝 ----

const submitAlipayForm = (html: string) => {
  // 将支付宝返回的 HTML 表单注入隐藏容器并自动提交，表单将自动跳转到支付宝页面
  const container = document.createElement('div')
  container.style.display = 'none'
  container.innerHTML = html
  document.body.appendChild(container)

  const form = container.querySelector('form')
  if (form) {
    form.target = '_blank'
    form.submit()
  }

  // 延迟清理 DOM，确保表单提交完成
  setTimeout(() => document.body.removeChild(container), 3000)
}

// ---- 创建订单 ----

const handleConfirm = async () => {
  if (creatingOrder.value || orderCreated.value || !props.purchaseSummary) return

  creatingOrder.value = true
  sdkError.value = ''
  buttonRenderError.value = ''

  try {
    const payload = {
      PlanId: props.planId,
      PaymentProvider: selectedProvider.value,
      Currency: currentCurrency.value,
      PaymentScene: paymentScene.value,
      RechargePoints: isPointsBusiness.value ? props.rechargePoints ?? props.purchaseSummary.points : undefined,
    }
    const res = await apiCreatePayment(payload)

    const payment: IPaymentPayload = res.Body.Payment

    pendingPayment.value = {
      OrderNo: res.Body.OrderNo,
      ChannelOrderId: payment.ChannelOrderId,
      PaymentProvider: payment.PaymentProvider,
      PayPalSdkUrl: payment.PayPalSdkUrl,
      createdAt: Date.now(),
    }

    orderCreated.value = true
    startPolling()

    if (selectedProvider.value === PaymentProvider.Alipay) {
      // PC 端（ALIPAY_PC）取 AlipayPageForm，移动端（PAYPAL_OR_ALIPAYH5）取 AlipayWapForm
      const alipayForm = paymentScene.value === PaymentScene.ALIPAY_PC ? payment.AlipayPageForm : payment.AlipayWapForm
      if (alipayForm) {
        submitAlipayForm(alipayForm)
      } else {
        sdkError.value = t('component.planPage.alipayFormMissing')
      }
    } else {
      await nextTick()
      await renderPayPalButtons()
    }
  } catch (error) {
    console.error('createPayment failed', error)
    orderCreated.value = false
  } finally {
    creatingOrder.value = false
  }
}

// ---- 弹窗开关 ----

const handleClose = () => {
  emit('update:visible', false)
}

// 弹窗关闭后重置所有状态（van-popup @closed 时触发）
const handleClosed = async () => {
  await resetState()
}

onBeforeUnmount(() => {
  clearPollTimer()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  void destroyPayPalButtons()
})
</script>

<template>
  <van-popup
    :close-on-click-overlay="false"
    :show="visible"
    :position="appStore.isMobile ? 'bottom' : 'center'"
    :round="true"
    :style="{
      width: appStore.isMobile ? '100%' : 'min(520px, calc(100vw - 32px))',
      borderRadius: popupRound,
      background: '#0b1020',
    }"
    @update:show="(val: boolean) => !val && handleClose()"
    @closed="handleClosed"
  >
    <div class="p-5 text-white sm:p-6">
      <!-- 头部 -->
      <section class="min-h-[50vh]">
        <div class="flex items-start justify-between gap-4">
          <div>
            <div class="text-sm text-white/45">{{ t('component.planPage.orderSummary') }}</div>
            <div class="mt-1 text-2xl font-semibold">{{ props.purchaseSummary?.title || '--' }}</div>
          </div>
          <button
            class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/8 text-white/60 transition hover:bg-white/15 hover:text-white"
            @click="handleClose"
          >
            <X :size="16" />
          </button>
        </div>

        <!-- 订单摘要卡片 -->
        <div class="mt-5 grid gap-3 rounded-3xl border border-white/8 bg-white/3 p-4 text-sm">
          <div v-if="pendingPayment" class="flex items-center justify-between text-white/65">
            <span>{{ t('component.planPage.orderNumber') }}</span>
            <span class="max-w-52 truncate text-white">{{ pendingPayment.OrderNo }}</span>
          </div>
          <div class="flex items-center justify-between text-white/65">
            <span>{{ t('component.planPage.paymentMethod') }}</span>
            <span class="text-white">{{
              selectedProvider === PaymentProvider.Alipay ? t('component.planPage.alipay') : 'PayPal'
            }}</span>
          </div>
          <div class="flex items-center justify-between text-white/65">
            <span>{{ t('component.planPage.paymentCurrency') }}</span>
            <span class="text-white">{{ paymentCurrencyLabel }}</span>
          </div>
          <div class="flex items-center justify-between text-white/65">
            <span>{{ isPointsBusiness ? t('component.planPage.rechargePointsLabel') : t('component.planPage.giftPointsLabel') }}</span>
            <span class="text-white">{{ props.purchaseSummary?.points ?? 0 }}</span>
          </div>
          <div class="h-px bg-white/8"></div>
          <div class="flex items-end justify-between">
            <span class="text-white/50">{{ t('component.planPage.totalAmount') }}</span>
            <span class="text-3xl font-semibold text-white">{{ displayPrice }}</span>
          </div>
          <p v-if="summaryHint" class="text-xs leading-5 text-white/45">{{ summaryHint }}</p>
        </div>

        <!-- 阶段 A：选择支付方式 -->
        <div v-if="!orderCreated" class="mt-5">
          <div class="mb-3 text-sm text-white/50">{{ t('component.planPage.selectPaymentMethod') }}</div>
          <div class="grid grid-cols-2 gap-3">
            <!-- 支付宝 -->
            <button
              class="flex items-center gap-3 rounded-2xl border px-4 py-3 transition-all duration-200"
              :class="
                selectedProvider === PaymentProvider.Alipay
                  ? 'border-purple-600/60 bg-purple-600/10 text-white'
                  : 'border-white/10 bg-white/3 text-white/60 hover:border-white/20 hover:bg-white/5'
              "
              @click="selectedProvider = PaymentProvider.Alipay"
            >
              <Icon icon="ant-design:alipay-outlined" color="#2f74fa" class="text-2xl shrink-0" />
              <span class="text-sm font-medium">{{ t('component.planPage.alipay') }}</span>
            </button>
            <!-- PayPal -->
            <button
              class="flex items-center gap-3 rounded-2xl border px-4 py-3 transition-all duration-200"
              :class="
                selectedProvider === PaymentProvider.PayPal
                  ? 'border-purple-600/60 bg-purple-600/10 text-white'
                  : 'border-white/10 bg-white/3 text-white/60 hover:border-white/20 hover:bg-white/5'
              "
              @click="selectedProvider = PaymentProvider.PayPal"
            >
              <Icon icon="tabler:brand-paypal-filled" color="#003087" class="text-2xl shrink-0" />
              <span class="text-sm font-medium">PayPal</span>
            </button>
          </div>
        </div>

        <!-- 阶段 B-Alipay：等待支付宝完成 -->
        <div
          v-if="orderCreated && selectedProvider === PaymentProvider.Alipay"
          class="mt-5 rounded-2xl border border-white/8 bg-white/3 px-4 py-5 text-center"
        >
          <Icon icon="ant-design:alipay-outlined" class="mx-auto mb-3 text-4xl text-[#1677FF]" />
          <p class="text-sm text-white/70">{{ t('component.planPage.alipayWaiting') }}</p>
          <button
            class="mt-4 flex items-center gap-2 mx-auto text-sm text-white/50 transition hover:text-white/80 disabled:opacity-40"
            :disabled="queryLoading"
            @click="queryPaymentStatus(true)"
          >
            <RefreshCcw :size="14" :class="{ 'animate-spin': queryLoading }" />
            {{ queryLoading ? t('component.planPage.querying') : t('component.planPage.refreshStatus') }}
          </button>
          <div v-if="sdkError" class="mt-3 text-xs text-red-400">{{ sdkError }}</div>
        </div>

        <!-- 阶段 B-PayPal：PayPal 按钮容器 -->
        <div
          v-if="orderCreated && selectedProvider === PaymentProvider.PayPal"
          class="mt-5 rounded-3xl bg-[#f2efea] p-4 text-slate-900"
        >
          <div ref="payPalContainer" class="min-h-14"></div>
          <div class="mt-2 text-center text-xs text-slate-500">
            <span v-if="isRenderingButton">
              <Loader2 :size="12" class="inline mr-1 animate-spin" />
              {{ t('component.planPage.loadingPayPalButton') }}
            </span>
            <span v-else>{{ t('component.planPage.paypalProvider') }}</span>
          </div>
          <div v-if="buttonRenderError" class="mt-2 text-center text-xs text-red-500">{{ buttonRenderError }}</div>
        </div>
      </section>

      <!-- 底部操作按钮 -->
      <div class="mt-5">
        <!-- 未创建订单：显示确认支付按钮 -->
        <button
          v-if="!orderCreated"
          class="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-purple-600 text-base font-semibold text-slate-900 transition hover:bg-purple-300 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="creatingOrder || !props.purchaseSummary"
          @click="handleConfirm"
        >
          <Loader2 v-if="creatingOrder" :size="18" class="animate-spin" />
          <Icon
            v-else-if="selectedProvider === PaymentProvider.Alipay"
            icon="ant-design:alipay-outlined"
            class="text-xl"
          />
          <Icon v-else icon="tabler:brand-paypal-filled" class="text-xl" />
          {{ confirmBtnLabel }}
        </button>

        <!-- PayPal 渲染中或渲染完成：引导提示按钮 -->
        <button
          v-else-if="selectedProvider === PaymentProvider.PayPal"
          class="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 text-sm text-white/50 disabled:cursor-not-allowed"
          disabled
        >
          <Loader2 v-if="isPayPalRendering" :size="16" class="animate-spin" />
          <template v-else>
            <Icon icon="logos:paypal" class="text-lg" />
            {{ t('component.planPage.paypalButtonGuide') }}
          </template>
        </button>

        <!-- 支付宝已提交：不显示底部按钮（等待区已有刷新操作） -->
      </div>
    </div>
  </van-popup>
</template>
